import { format, parseISO } from 'date-fns';

import { getDateFnsLocale } from 'utils/date';
import { displayNumber } from 'utils/misc';

import {
  HOURS_DECIMALS,
  HOURS_YEARS_AHEAD,
  MIN_HOURS_YEAR,
  SUMMARY_FIELDS
} from './EmploymentHours.consts';

import type {
  EmploymentHoursGridRow,
  EmploymentHoursRow,
  WabSettings
} from './EmploymentHours.types';

/**
 * Formats an hours value. Returns '' for null/undefined — NOT '0,00'.
 *
 * This is the single most important function in the screen: the backend drops every zero-valued
 * field (`EmployeeDelegate.groovy:563`), so ~60% of cells arrive as `undefined`, and Dojo rendered
 * those blank (`EmployeeWorkAgreementsGridColumns.js:32-37`). Formatting them as '0,00' would fill
 * the grid with fake zeros and no type error would catch it.
 *
 * Unlike Dojo's `'#.00'` pattern this uses the locale number format, so totals over 999 gain a
 * thousands separator (nl: `1.800,00`). Deliberate improvement; monthly values never reach 1000.
 */
export const formatHours = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '';
  }

  // Dojo guarded against rendering '-0.00' (GridColumns.js:57). Do it numerically so it holds in
  // every locale rather than string-replacing a hard-coded '-0.00'.
  const rounded = Math.abs(value) < 0.5 / 10 ** HOURS_DECIMALS ? 0 : value;

  return displayNumber(rounded, HOURS_DECIMALS);
};

/** Renders one end of a row's validity as dd-MM, matching Dojo's date pattern. */
export const formatPeriodMonth = (isoDate?: string | null): string => {
  if (!isoDate) {
    return '';
  }

  const parsed = parseISO(isoDate);

  // Dojo forgot to return after clearing the node, then called stamp.fromISOString(undefined)
  // (GridColumns.js:165-183). Guard properly instead.
  return Number.isNaN(parsed.getTime())
    ? ''
    : format(parsed, 'MMM', { locale: getDateFnsLocale() });
};

/**
 * Year options, newest first: currentYear + 2 down to 2015, filtered to the years the backend
 * reports as valid. Ported from `EmployeeWorkAgreementsHours.js:413-427`.
 */
export const buildYearOptions = (
  validYears?: number[],
  now = new Date()
): number[] => {
  const currentYear = now.getFullYear();
  const years: number[] = [];

  for (
    let candidate = currentYear + HOURS_YEARS_AHEAD;
    candidate >= MIN_HOURS_YEAR;
    candidate--
  ) {
    if (!validYears || validYears.includes(candidate)) {
      years.push(candidate);
    }
  }

  return years;
};

/**
 * Keeps `preferred` when it is valid, otherwise picks the valid year closest to the current year.
 * Ported from `EmployeeWorkAgreementsHours.js:176-183`; the same snap exists in
 * `CostCalculationExternal.tsx:48-55`.
 */
export const snapToNearestValidYear = (
  preferred: number | undefined,
  validYears: number[] | undefined,
  now = new Date()
): number | undefined => {
  if (!validYears?.length) {
    return undefined;
  }

  const currentYear = now.getFullYear();
  const candidate = preferred ?? currentYear;

  if (validYears.includes(candidate)) {
    return candidate;
  }

  // Sort a copy: Dojo sorted the array it had just been handed, mutating the response.
  return [...validYears].sort(
    (left, right) =>
      Math.abs(currentYear - left) - Math.abs(currentYear - right)
  )[0];
};

/**
 * The pinned bottom totals row. Sums only the 11 columns Dojo's footer summed
 * (`EmployeeWorkAgreementsHours.js:311-333`), using `?? 0` because rows are sparse.
 *
 * Stores RAW sums — the sign flip is applied by the balance cell, not here. Dojo flipped the sign of
 * `hoursBalance` in the footer but of BOTH balance columns in the cells, so the footer's
 * `hoursBalanceCumReal` disagreed with the column above it. `GridColumns.js:48` carries the TODO
 * asking for exactly this fix; unifying it changes that one displayed value.
 */
export const computeTotalsRow = (
  rows: EmploymentHoursRow[]
): EmploymentHoursGridRow => {
  const totals: Record<string, number> = {};

  for (const row of rows) {
    for (const field of SUMMARY_FIELDS) {
      totals[field] = (totals[field] ?? 0) + (row[field] ?? 0);
    }
  }

  return { id: 'totals', isTotals: true, ...totals };
};

/**
 * Pulls the WAB warning/alert levels out of the `wab` admin settings group.
 * Returns null when either is missing so the grid still renders, just without WAB colouring —
 * Dojo showed an error and carried on (`EmployeeWorkAgreementsHours.js:246-271`).
 */
export const parseWabSettings = (
  response?: { settings?: { key: string; value?: string }[] }[]
): WabSettings | null => {
  const settings = response?.[0]?.settings;

  if (!settings?.length) {
    return null;
  }

  const level = (key: string) => {
    const raw = settings.find((setting) => setting.key === key)?.value;
    // Number('') is 0, not NaN — an empty (never configured) level must not become a real threshold
    const parsed = raw === undefined || raw === '' ? Number.NaN : Number(raw);

    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const warningLevel = level('warning.level');
  const alertLevel = level('alert.level');

  if (warningLevel === undefined || alertLevel === undefined) {
    return null;
  }

  return { warningLevel, alertLevel };
};

/**
 * Turns API rows into grid rows. The payload has no id and `dutyLineMngId` is nullable (the query
 * RIGHT JOINs dty_duty_line_year), so the payroll period is the id, with the index as a fallback.
 */
export const toGridRows = (
  rows: EmploymentHoursRow[],
  wabSettings: WabSettings | null
): EmploymentHoursGridRow[] =>
  rows.map((row, index) => ({
    ...row,
    id: row.pPeriod ?? index + 1,
    isTotals: false,
    wabSettings
  }));
